/*
 * Copyright (c) 2021 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as _ from 'underscore';
import { Logger, MessageService } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '@app/common/app-config.service';
import { CustomWorkListService } from '@app/ui/common/work-list-navigator/custom-work-list.service';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';
import { BehaviorSubject, Observable } from 'rxjs';

const log = new Logger('SearchFilterCriteriaService');

@Injectable({
  providedIn: 'root'
})
export class SearchFilterCriteriaService {
  // private triggerPreload;

  private criteria = {
    // common
    modality: [],
    authors: {},
    orderingLocation: [],
    departments: [],
    equipments: [],
    sites: [],
    aiFinding: [],
    examStatuses: [],
    supportedReportStatuses: [],
    reportStatus: {},
    instituteName: [],
    priority: [],
    exceptions: [],

    // custom
    bodyParts: [],
    specialties: [],
    patientLocation: [],
    protocolStatus: [],
    examDescriptions: [],

    // inline
    status: [],
    imageCount: [],
    encounterClass: [],

    // Users
    usrNameMap: {},
    assignee: [],
    vipAssignee: [],
    referringPhysician: {},

    verifiedTimeOfExam: [],
    gender: [
      { code: 'M', name: 'Male' },
      { code: 'F', name: 'Female' },
      { code: 'O', name: 'Other' },
      { code: 'Unknown', name: 'Unknown' }
    ]
  };

  currentUser: any;

  // Observers
  filterCriteriaLoaded = false;
  private loadedObserver: BehaviorSubject<boolean> = new BehaviorSubject(this.filterCriteriaLoaded);
  getLoadedObserver(): Observable<boolean> {
    return this.loadedObserver.asObservable();
  }

  constructor(
    private messageService: MessageService,
    private dataService: ProxyDataService,
    private appConfigService: AppConfigService,
    private customWorklistService: CustomWorkListService,
    private translate: TranslateService
  ) {
    this.broadCastListeners();
  }

  getData(dataName: string): any {
    return this.criteria[dataName] || {};
  }

  getExamDescriptions(searchCriteria, isWildCard = true, groupByDesc = true) {
    return this.loadExamDescriptions(searchCriteria, isWildCard, groupByDesc);
  }

  getExamStatuses() {
    return this.criteria.examStatuses;
  }

  getPatientLocations(): any[] {
    return _.values(this.criteria.patientLocation);
  }

  getBodyParts(): any[] {
    return _.values(this.criteria.bodyParts);
  }

  getExamSpecialties(): any[] {
    return _.values(this.criteria.specialties);
  }

  getDepartments(): any[] {
    return _.values(this.criteria.departments);
  }

  getProtocolStatuses(): any[] {
    return _.values(this.criteria.protocolStatus);
  }

  getModalities(): any[] {
    return _.values(this.criteria.modality);
  }

  getOrderingLocations(): any[] {
    return _.values(this.criteria.orderingLocation);
  }

  getRadiologists(): any[] {
    return _.values(this.criteria.authors);
  }

  getInstitutes(): any[] {
    return _.values(this.criteria.instituteName);
  }

  getReportStatuses(): any[] {
    return _.values(this.criteria.reportStatus);
  }

  getEquipment(): any[] {
    return _.values(this.criteria.equipments);
  }

  getSites(): any[] {
    return _.values(this.criteria.sites);
  }

  getAiFindings(): any[] {
    return _.values(this.criteria.aiFinding);
  }

  getReferringPhysicians(): any[] {
    return _.values(this.criteria.referringPhysician);
  }

  getFilterData(criteriaName) {
    if (_.contains(['assignee', 'vipAssignee'], criteriaName)) {
      const assignee = {};
      _.each(this.criteria[criteriaName], userName => {
        if (this.criteria.usrNameMap[userName]) {
          const user = this.criteria.authors[this.criteria.usrNameMap[userName]];
          assignee[userName] = {
            id: user.userName,
            name: user.name
          };
        }
      });
      return assignee;
    }
    return this.criteria[criteriaName];
  }

  getFilterDataById(criteriaName, filterId) {
    return this.criteria[criteriaName] ? this.criteria[criteriaName][filterId] : undefined;
  }

  /**
   * Returns gender object matching the provided code
   * @param code - Gender code to search for ('M', 'F', 'O', 'Unknown')
   * @returns The matching gender object if found, otherwise returns the 'Unknown' gender object. Returns null if code is not provided.
   */
  getGenderByCode(code: string) {
    if (!code) {
      return null;
    }
    const gender = this.criteria.gender.find(_g => _g.code?.toLowerCase() === code.toLowerCase());
    return gender ? gender : this.criteria.gender.find(_g => _g.code === 'Unknown');
  }

  getAuthor(authId) {
    return authId && (this.criteria.authors[authId] || this.criteria.referringPhysician[authId]);
  }

  getAuthorByUserName(userName: string) {
    return userName && this.getAuthor(this.criteria.usrNameMap[userName]);
  }

  getAuthorUserName(authId) {
    const author = this.criteria.authors[authId] || this.criteria.referringPhysician[authId];
    if (!!author) {
      return author.userName;
    }
    return undefined;
  }

  getAssignee(isAssigneeVip = false, instituteId = undefined) {
    let criteriaName = isAssigneeVip ? 'vipAssignee' : 'assignee';
    if (instituteId !== undefined) {
      criteriaName += '_' + instituteId;
    }
    const assignee = [];
    _.each(this.criteria[criteriaName], userName => {
      if (this.criteria.usrNameMap[userName]) {
        assignee.push(this.criteria.authors[this.criteria.usrNameMap[userName]]);
      }
    });
    return assignee;
  }

  getExamPriorities() {
    return this.criteria.priority;
  }

  getPriorityById(id) {
    return _.find(this.criteria.priority, priorityObj => {
      return priorityObj.priorityId === id;
    });
  }

  getverifiedTimeOfExam() {
    return this.criteria.verifiedTimeOfExam;
  }

  getExceptions(): any[] {
    return _.values(this.criteria.exceptions);
  }

  preloadCriteria() {
    Promise.all([
      this.preloadFilters(),
      this.preloadExamStatus(),
      this.preloadExamPriorities(),
      this.preloadUsersFilters(),
      this.loadAssignees()
    ]).then(() => {
      this.addCurrentUserToAssigneeList();
      this.filterCriteriaLoaded = true;
      this.loadedObserver.next(this.filterCriteriaLoaded);
      this.messageService.broadcast('preloadSearchCriteria.done');
    });
  }

  // Specific API endpoint call
  preloadExamStatus() {
    return this.customWorklistService.getExamStatusDetails().then(
      (statuses: any) => {
        this.criteria.examStatuses = statuses.map(status => {
          return {
            examStatusNumber: status.examStatusNumber,
            examStatusDescription: status.examStatusDescription,
            examStatusId: status.examStatusId
          };
        });
      },
      err => {
        this.logError('customWorklistService.getExamStatusDetails', err);
      }
    );
  }

  preloadExamPriorities() {
    return this.getEndpoint(appConstants.appURLs.priorities)
      .then((priorities: any) => {
        let allPriorities = [];
        let disabledPriorities = [];
        _.each(priorities, (item: any) => {
          item.examPriorityDesc = UtilService.getLocalizedPriority(item.examPriorityDesc, this.translate);

          if (item.priorityEnable === true) {
            allPriorities.push(item);
          } else if (item.priorityEnable === false) {
            disabledPriorities.push(item);
          }
        });
        allPriorities = _.sortBy(allPriorities, 'examPriorityDesc');
        disabledPriorities = _.sortBy(disabledPriorities, 'examPriorityDesc');

        _.each(disabledPriorities, item => {
          allPriorities.push(item);
        });

        this.criteria.priority = allPriorities.map(priority => {
          return {
            priorityId: Number(priority.examPriorityCode),
            priorityName: priority.examPriorityDesc,
            counterValue: priority.counterFlag,
            isPriorityEnabled: priority.priorityEnable,
            id: Number(priority.examPriorityCode),
            name: priority.examPriorityDesc,
            priorityBadgeColor: _.isNull(priority.priorityBadgeColor) ? '' : priority.priorityBadgeColor
          };
        });
      })
      .catch(ex => {
        log.error('Exception occurred while getting exam priorities, Exception - ' + JSON.stringify(ex));
      });
  }

  loadExamDescriptions(searchCriteria, isSearchCriteriaWildCard, groupByDesc) {
    return this.customWorklistService.getExamDescriptions(searchCriteria, isSearchCriteriaWildCard).then(
      (descriptions: any) => {
        return groupByDesc ? this.getExamDescsByGroupedDesc(descriptions) : this.getExamDescs(descriptions);
      },
      err => {
        this.logError('customWorklistService.getExamDescriptions', err);
      }
    );
  }

  private getExamDescs(descriptions: any) {
    this.criteria.examDescriptions = descriptions.map(description => {
      return {
        id: description.procedureCode,
        name: description.searchProcedureDescription,
        active: description.active,
        modality: description.modality
      };
    });
    return this.criteria.examDescriptions;
  }

  private getExamDescsByGroupedDesc(descriptions: any) {
    const groupedDesc = _.groupBy(descriptions, 'searchProcedureDescription');
    this.criteria.examDescriptions = _.map(groupedDesc, (val, key) => {
      return {
        id: _.pluck(val, 'procedureId').join(','),
        name: key
      };
    });
    return this.criteria.examDescriptions;
  }

  // Assignee Users
  loadAssignees(isPrivileged = false, instituteId = undefined) {
    return new Promise((resolve, reject) => {
      let storeRef = isPrivileged ? 'vipAssignee' : 'assignee';
      const params: any = { privilegeRadsOnly: isPrivileged };
      if (instituteId !== undefined) {
        params.instituteId = instituteId;
        storeRef += '_' + instituteId;
      }
      this.dataService
        .get(appConstants.appURLs.radiologists, params)
        .then(async (assignRoleUsers: string[]) => {
          this.criteria[storeRef] = assignRoleUsers;
          this.addCurrentUserToAssigneeList(storeRef);

          if (
            !_.isEmpty(this.criteria.authors) &&
            _.difference(assignRoleUsers, _.pluck(this.criteria.authors, 'userName')).length
          ) {
            await this.preloadUsersFilters(typeof instituteId !== 'number');
          }

          resolve(true);
        })
        .catch(ex => {
          log.error(
            'Exception occurred while getting assignee user ' + storeRef + ', filter. Exception - ' + JSON.stringify(ex)
          );
          reject(ex);
        });
    });
  }

  private addCurrentUserToAssigneeList(storeRef = 'assignee') {
    // Add currentuser if not part of the assignee list
    this.currentUser = this.appConfigService.getUserDto();
    if (this.currentUser && !_.contains(this.criteria[storeRef], this.currentUser.userName)) {
      this.criteria[storeRef].push(this.currentUser.userName);
    }
  }

  // Referring & all users
  preloadUsersFilters(triggerBroadcastWhenDone = true) {
    return this.getEndpoint(appConstants.appURLs.defaultWorkListFilters, {
      filterParamDTOList: [{ filterName: 'referringPhysician' }, { filterName: 'assignee' }]
    })
      .then((filters: any) => {
        // Referring physician
        const transformedReferringData = {};
        _.each(filters.referringPhysician, (value, key) => {
          transformedReferringData[key] = {
            id: value.userId,
            name: value.name,
            role: value.role,
            instituteIds: value.instituteIds
          };
        });
        this.criteria.referringPhysician = transformedReferringData;

        // All users data - make sure current user is present
        const transformedAuthorData = {};
        _.each(filters.assignee, value => {
          transformedAuthorData[value.userId] = {
            id: value.userId,
            name: value.name,
            role: value.role,
            userName: value.userName,
            instituteIds: value.instituteIds,
            active: value.active
          };
          this.criteria.usrNameMap[value.userName] = value.userId;
        });
        this.criteria.authors = transformedAuthorData;
        this.updateCurrentUserLabelOnUsersFilter();
        if (triggerBroadcastWhenDone) {
          this.messageService.broadcast('preloadUserFilters.done');
        }
      })
      .catch(ex => {
        log.error(
          'Exception occurred while getting users - referring & all - filters, Exception - ' + JSON.stringify(ex)
        );
      });
  }

  // Process the users data
  private updateCurrentUserLabelOnUsersFilter() {
    this.currentUser = this.appConfigService.getUserDto();
    if (this.currentUser) {
      // All users should contain the current user (used in assignExam / folderMaintenance).
      if (this.criteria.authors[this.currentUser.userId] === undefined) {
        this.criteria.authors[this.currentUser.userId] = {
          id: this.currentUser.userId,
          userName: this.currentUser.userName,
          name: this.formatUserForNameDisplay(this.currentUser)
        };
        this.criteria.usrNameMap[this.currentUser.userName] = this.currentUser.userId;
      } else {
        this.addYouLabelForUser(this.criteria.authors, this.currentUser.userId);
      }

      this.addYouLabelForUser(this.criteria.referringPhysician, this.currentUser.userId);
    }
  }

  private formatUserForNameDisplay(user) {
    if (!_.isEmpty(user.firstName) && !_.isEmpty(user.lastName)) {
      return [user.lastName, user.firstName].join(', ');
    } else if (!_.isEmpty(user.firstName)) {
      return user.firstName;
    } else {
      return user.lastName;
    }
  }

  private addYouLabelForUser(data, id) {
    const labelSelf = this.translate.instant('label.you');
    if (data[id]) {
      data[id].name = [data[id].name, ' (', labelSelf, ')'].join('');
    }
  }

  preloadFilters() {
    return this.getEndpoint(appConstants.appURLs.defaultWorkListFilters, {
      filterParamDTOList: [
        { filterName: 'orderingLocation' },
        { filterName: 'modality' },
        { filterName: 'equipments' },
        { filterName: 'sites' },
        { filterName: 'aiFinding' },
        { filterName: 'patientLocation' },
        { filterName: 'departments' },
        { filterName: 'bodyParts' },
        { filterName: 'specialties' },
        { filterName: 'protocolStatus' },
        { filterName: 'instituteName' },
        { filterName: 'reportStatus' },
        { filterName: 'status' },
        { filterName: 'imageCount' },
        { filterName: 'encounterClass' },
        { filterName: 'exceptions' }
      ]
    })
      .then((filters: any) => {
        this.transformList('modality', filters.modality);
        this.transformList('bodyParts', filters.bodyParts);
        this.transformList('aiFinding', filters.aiFinding);
        this.transformList('patientLocation', filters.patientLocation);
        this.transformList('specialties', filters.specialties);
        this.transformList('status', filters.status);
        this.transformList('encounterClass', filters.encounterClass);
        this.transformListValue('sites', filters.sites);

        // List for Image Count
        _.each(filters.imageCount, (value, key) => {
          const hideZeroImg = this.translate.instant(value);
          filters.imageCount[key] = { id: key, name: hideZeroImg };
        });
        this.criteria.imageCount = filters.imageCount;

        // List for exception
        _.each(filters.exceptions, (value, key) => {
          const exceptionName = this.translate.instant('exception.' + value);
          filters.exceptions[key] = { id: Number(key), name: exceptionName };
        });
        this.criteria.exceptions = filters.exceptions;

        // List of ordering location
        if (
          filters.orderingLocation &&
          filters.orderingLocation[0] &&
          _.isEqual(filters.orderingLocation[0], 'Unspecified')
        ) {
          filters.orderingLocation[0] = this.translate.instant('workloadManager.listContainer.noInformationMsg');
        }
        this.transformList('orderingLocation', filters.orderingLocation);

        //List of equipments
        if (filters.equipments) {
          const nullKeys = Object.keys(filters.equipments).filter(k => filters.equipments[k] === 'NULL_VALUE');
          _.each(nullKeys, key => {
            filters.equipments[key] = this.translate.instant('workloadManager.listContainer.noInformationMsg');
          });
          const emptyKeys = Object.keys(filters.equipments).filter(k => filters.equipments[k] === 'EMPTY_VALUE');
          _.each(emptyKeys, key => {
            filters.equipments[key] = '';
          });
        }

        this.transformList('equipments', filters.equipments);

        // List of protocolStatus
        if (this.appConfigService.getProtocolEnabled() && !_.isEmpty(filters.protocolStatus)) {
          this.criteria.protocolStatus = filters.protocolStatus;
        }

        // List of instituteName
        _.each(filters.instituteName, (value, key) => {
          filters.instituteName[key] = {
            id: Number(key),
            instituteId: Number(key),
            name: value.name,
            instituteName: value.name,
            code: value.code
          };
        });
        this.criteria.instituteName = filters.instituteName;

        // List of reportStatus
        _.each(filters.reportStatus, (value, key) => {
          const reportStatus = this.translate.instant('report.status.' + key);
          filters.reportStatus[key] = {
            id: Number(key),
            reportStatusId: Number(key),
            name: reportStatus,
            reportStatusDescription: reportStatus
          };
        });
        this.criteria.reportStatus = filters.reportStatus;

        // List of Department
        _.each(filters.departments, (value, key) => {
          filters.departments[key] = value;
        });
        this.criteria.departments = filters.departments;
      })
      .catch(ex => {
        log.error('Exception occurred while getting default filters, Exception - ' + JSON.stringify(ex));
      });
  }

  updateSupportedReportStatus(data) {
    const supportedReportStatuses = _.keys(data);
    if (!_.isEmpty(supportedReportStatuses)) {
      log.debug('supported statuses = ', JSON.stringify(supportedReportStatuses));
      const reportStatus = {};
      _.each(supportedReportStatuses, status => {
        const text = this.translate.instant('report.status.' + status);
        const item = this.criteria.reportStatus[status];
        item['name'] = item['reportStatusDescription'] = text;
        reportStatus[status] = item;
      });
      log.debug('final report status = ', JSON.stringify(reportStatus, null, 2));
      this.criteria.reportStatus = reportStatus;
    }
    this.criteria.supportedReportStatuses = supportedReportStatuses;
    return _.values(this.criteria.reportStatus);
  }

  private transformList(criteriaName, criteriaData) {
    _.each(criteriaData, (value, key) => {
      criteriaData[key] = { id: key, name: value };
    });
    this.criteria[criteriaName] = criteriaData;
  }

  private transformListValue(criteriaName, criteriaData) {
    const transformedData = {};
    _.each(criteriaData, (value: any) => {
      transformedData[value] = { id: value, name: value };
    });
    this.criteria[criteriaName] = transformedData;
  }

  private getEndpoint(endpoint, requestBody?) {
    const promise = requestBody ? this.dataService.create(endpoint, requestBody) : this.dataService.get(endpoint);
    promise.catch(error => {
      this.logError(endpoint, error);
    });
    return promise;
  }

  private logError(endpoint, err) {
    log.error(["Error Getting '", endpoint, "':"].join(''), JSON.stringify(err));
  }

  private broadCastListeners() {
    const workListLoadedListener = this.messageService.on('worklistLoaded.done').subscribe(() => {
      workListLoadedListener.unsubscribe();
      this.preloadCriteria();
    });
  }

  public findByvalue(criteriaName, criteriafield, criteriaData): Promise<any> {
    return new Promise(resolve => {
      this.getLoadedObserver().subscribe(lookUpLoaded => {
        if (lookUpLoaded) {
          const cdata = _.values(this.criteria[criteriaName]).find(item => {
            return item[criteriafield]?.toLowerCase() === criteriaData.toLowerCase();
          });
          resolve(cdata);
        }
      });
    });
  }
}
